import { useEffect, useState } from "react"

import { Box, CircularProgress, Dialog, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import IframeResizer from "iframe-resizer-react"
import { IconButton, SvgIcon, type iconKeys, type FC } from ".."

const perks = [
  {
    title: "Exclusive Savings",
    icon: "tag",
  },
  {
    title: "Insider Contests",
    icon: "present",
  },
  {
    title: "Trending Guides",
    icon: "bookmark",
  },
]

type NewsletterModalProps = {
  open: boolean
  onClose: () => void
}

const NewsletterModal: FC<NewsletterModalProps> = ({ open, onClose }) => {
  const [iframeReady, setIframeReady] = useState(false)

  useEffect(() => {
    setIframeReady(false)
  }, [onClose])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="body"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: (theme) => alpha(theme.palette["neutral.900"].main, 0.4),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          maxWidth: 1266,
          borderRadius: 1.5,
          zIndex: 4100,
        },
      }}
      sx={{
        zIndex: 4000,
      }}
    >
      <IconButton
        variant="secondary"
        onClick={onClose}
        icon={
          <SvgIcon
            variant="close"
            strokeMode
            sx={{ stroke: (theme) => theme.palette["secondary.800"].main }}
            size={16}
          />
        }
        sx={(theme) => ({
          width: { xs: 16, md: 52 },
          height: { xs: 16, md: 52 },
          position: "absolute",
          top: 20,
          right: 20,
          zIndex: 1,

          [theme.breakpoints.down("md")]: {
            "border": 0,

            "&:hover, &:focus-within": {
              bgcolor: "transparent",
            },
          },
        })}
      />
      <Box
        display="grid"
        position="relative"
        minHeight={{ lg: 700 }}
        gridTemplateColumns={{ xs: "1fr", lg: "clamp(0px, 36.5%, 462px) 1fr" }}
        columnGap={0}
        rowGap={0}
      >
        <Box
          px={{ xs: 6, lg: 20 }}
          py={{ xs: 10, lg: 20 }}
          bgcolor={(theme) => theme.palette["secondary.200"].main}
          color={(theme) => theme.palette["secondary.800"].main}
        >
          <Typography variant="heading.xl" component="h3" mb={5}>
            Sign Up
          </Typography>
          <Typography variant="body.md" component="p">
            Join our newsletter to access Travel Specials, Inspiration, and Expert Guides.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row", lg: "column" }}
            rowGap={{ xs: 5, lg: 10 }}
            columnGap={10}
            flexWrap="wrap"
            mt={{ xs: 5, lg: 17.5 }}
          >
            {perks.map(({ icon, title }, i) => (
              <Stack direction="row" key={i} alignItems="center" columnGap={3.5}>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: { xs: 14, lg: 20 },
                    aspectRatio: "1",
                  }}
                >
                  <SvgIcon variant={icon as iconKeys} size={{ xs: 16, lg: 18 }} />
                </Stack>
                <Typography variant="body.sm" component="h2">{title}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
        <Box px={{ xs: 6, lg: 23.5 }} pb={{ xs: 10, lg: 18 }} pt={{ xs: 10, lg: 22 }}>
          {!iframeReady && (
            <Stack alignItems="center">
              <CircularProgress />
            </Stack>
          )}
          <Box
            component={IframeResizer}
            src="https://forms.goway.com/t4-newsletter"
            onResized={() => setIframeReady(true)}
            sx={{
              border: 0,
              width: "1px",
              minWidth: "100%",
              opacity: iframeReady ? 1 : 0,
              height: iframeReady ? "auto" : 0,
            }}
          />
        </Box>
      </Box>
    </Dialog>
  )
}
export default NewsletterModal
